import { ITEM_TYPES } from '../constants';
import {
    amortizeDebtMonth,
    applyTimelineEvents,
    initSimulatedDebt,
    itemMonthlyCashflow,
    liquidMonthlyInterest,
    monthlyAmount,
    monthsBetween,
    resolveItemRates,
    stepItemMonth,
} from './engine';
import { getFireInclusion, getFirePassive } from './fire';

// F1 — the projection chart, and the FIRE series that rides along with it.
//
// This file owns the *shape* of the projection: the month loop, the labels,
// inflation discounting, the FIRE crossings and the drawdown. The portfolio
// arithmetic it runs on — what a month does to an item, what an item
// contributes to cashflow, how debt amortizes, how a timeline event settles —
// belongs to ./engine and is shared with the dashboard, F28 and Monte Carlo.

// Relative epsilon for FI-state crossing comparisons (avoids 1-month jitter zones).
const FIRE_EPS = 1e-9;

/**
 * Apply the timeline events landing in `currentDate`'s month to an item list.
 *
 * The item-only counterpart of engine/applyTimelineEvents: no cash changes
 * hands, because the caller below tracks no cash for anything to settle
 * against. Returns a new list.
 */
const applyItemOnlyEvents = (items, events, currentDate) => events.reduce((current, event) => {
    const eventDate = new Date(event.eventDate);
    const landsThisMonth = eventDate.getFullYear() === currentDate.getFullYear()
        && eventDate.getMonth() === currentDate.getMonth();
    if (!landsThisMonth) return current;

    switch (event.eventType) {
        case 'add': {
            const id = event.itemData.id || event.id;
            return current.some(item => item.id === id)
                ? current
                : [...current, { ...event.itemData, id, isActive: true, simulatedLoanBalance: 0, simulatedMortgageBalance: 0 }];
        }
        case 'update':
            return current.map(item => item.id === event.targetItemId ? { ...item, ...event.itemDataUpdate } : item);
        case 'delete':
            return current.filter(item => item.id !== event.targetItemId);
        default:
            return current;
    }
}, items);

/**
 * One item's state at a future date, for the timeline modal's preview.
 *
 * Deliberately narrower than generateProjectionData: it tracks no cash, so a
 * planned event only has to add, change or remove items. The per-month step is
 * the kernel's, so this preview and the chart cannot disagree about an item.
 */
export const getProjectedItemStateAtDate = (baseItem, allPortfolioItems, allTimelineEvents, targetDate) => {
    if (!baseItem) return null;

    const now = new Date();
    const monthsToProject = Math.floor(monthsBetween(now, targetDate));
    if (monthsToProject <= 0) return baseItem;

    // Items a timeline event will add are seeded up front so the loop can find
    // them by id; the 'add' case then only fills in ones that arrive from an
    // event carrying no explicit item id.
    const plannedAdditions = allTimelineEvents
        .filter(e => e.eventType === 'add')
        .map(e => ({ ...e.itemData, id: e.itemData.id || e.id }));

    let items = JSON.parse(JSON.stringify([...allPortfolioItems, ...plannedAdditions]));
    items.forEach(item => initSimulatedDebt(item, now));

    const activeEvents = allTimelineEvents.filter(event => !event.isDisabled);

    for (let i = 0; i < monthsToProject; i++) {
        const currentDate = new Date(now.getFullYear(), now.getMonth() + i + 1, 1);
        items = applyItemOnlyEvents(items, activeEvents, currentDate);
        items.forEach(item => {
            stepItemMonth(item);
            amortizeDebtMonth(item);
        });
    }

    return items.find(item => item.id === baseItem.id) || null;
}

export const generateProjectionData = ({ basePortfolioItems, liquidAssets, liquidAssetsInterestRate, projectionYears, dashboardSettings, timelineEvents, rateOverrides = {}, locale = 'en', nowLabel = 'Now' }) => {
    const projection = [];
    let currentSimulatedLiquidAssets = parseFloat(liquidAssets) || 0;
    const { isProjectionInflationAdjusted, projectionInflationRate, isScenarioActive, isProjectionGross } = dashboardSettings;
    const fireWithdrawalRate = typeof dashboardSettings.fireWithdrawalRate === 'number' ? dashboardSettings.fireWithdrawalRate : 0.04;
    const fireExpenseOverrideEnabled = !!dashboardSettings.fireExpenseOverrideEnabled;
    const fireExpenseOverrideAmount = parseFloat(dashboardSettings.fireExpenseOverrideAmount) || 0;
    const fireInflationRate = typeof projectionInflationRate === 'number' && projectionInflationRate >= 0 ? projectionInflationRate : 0;

    let simulatedPortfolioItems = JSON.parse(JSON.stringify(basePortfolioItems));

    const simulationStart = new Date();
    simulatedPortfolioItems.forEach(item => initSimulatedDebt(item, simulationStart));

    const t0_portfolioValue = simulatedPortfolioItems.reduce((sum, item) => {
        let value = 0;
        if (item.type === ITEM_TYPES.REAL_ESTATE || item.type === ITEM_TYPES.OTHER_INVESTMENT) {
            value = parseFloat(item.currentValue) || 0;
            if (!isProjectionGross && item.hasMortgage) {
                value -= item.simulatedMortgageBalance;
            }
        } else if (item.type === ITEM_TYPES.LOAN && !isProjectionGross) {
            value = -item.simulatedLoanBalance;
        }
        return sum + value;
    }, 0);

    const liquidAssetsNum = parseFloat(liquidAssets) || 0;
    const t0_liquidAssetsInterest = liquidMonthlyInterest(liquidAssetsNum, liquidAssetsInterestRate);
    
    // Read the seeded items, whose remaining terms are real, rather than the
    // raw ones with the gate forced open. A debt that finished before today is
    // not paid at t0 — which is also what the dashboard now reports, so "Now"
    // and month 1 line up instead of stepping apart.
    const t0_portfolioCashflow = simulatedPortfolioItems.reduce(
        (sum, item) => sum + itemMonthlyCashflow(item, { mode: 'simulated' }), 0);

    const t0_totalCashflow = t0_portfolioCashflow + t0_liquidAssetsInterest;
    
    const formatMonthYear = (date) => {
        try {
            return date.toLocaleDateString(locale || undefined, { month: 'short', year: 'numeric' });
        } catch {
            return `${date.getMonth() + 1}/${date.getFullYear()}`;
        }
    };

    // --- t0 (now) FIRE figures ---
    let t0_fireAssets = currentSimulatedLiquidAssets;
    let t0_weightedRateNum = currentSimulatedLiquidAssets * (parseFloat(liquidAssetsInterestRate) || 0); // Σ(asset × annual rate), for the blended Coast-FIRE return
    let t0_monthlyExpenses = 0; // common: living + non-rented property costs
    let t0_monthlyFullDebt = 0; // full debt payment (income view)
    let t0_monthlyPassive = t0_liquidAssetsInterest;
    simulatedPortfolioItems.forEach(fi => {
        const fiVal = parseFloat(fi.currentValue || 0) || 0;
        switch (fi.type) {
            case ITEM_TYPES.REAL_ESTATE:
                if (getFireInclusion(fi)) {
                    const t0Equity = fiVal - (fi.simulatedMortgageBalance || 0);
                    t0_fireAssets += t0Equity;
                    t0_weightedRateNum += t0Equity * (parseFloat(fi.annualAppreciationRate) || 0);
                }
                if (fi.isRented) {
                    if (getFirePassive(fi)) t0_monthlyPassive += monthlyAmount(fi.monthlyRentalIncome, fi.amountFrequency) || 0;
                } else {
                    t0_monthlyExpenses += monthlyAmount(fi.monthlyCosts, fi.amountFrequency) || 0;
                }
                if (fi.hasMortgage && fi.simulatedMortgageTermRemaining > 0) {
                    t0_monthlyFullDebt += parseFloat(fi.mortgageMonthlyPayment || 0) || 0;
                }
                break;
            case ITEM_TYPES.OTHER_INVESTMENT:
                if (getFireInclusion(fi)) {
                    t0_fireAssets += fiVal;
                    t0_weightedRateNum += fiVal * (fi.isGrowth ? (parseFloat(fi.annualGrowthRate) || 0) : 0);
                }
                if (fi.isIncome && getFirePassive(fi)) t0_monthlyPassive += (fiVal * (parseFloat(fi.annualIncomeYield || 0) || 0)) / 12;
                break;
            case ITEM_TYPES.EXPENSE:
                t0_monthlyExpenses += monthlyAmount(fi.monthlyExpense, fi.amountFrequency) || 0;
                break;
            case ITEM_TYPES.LOAN:
                t0_fireAssets -= (fi.simulatedLoanBalance || 0); // FIRE/asset view = net worth: deduct all debt
                if (fi.simulatedLoanTermRemaining > 0) {
                    t0_monthlyFullDebt += parseFloat(fi.monthlyPayment || 0) || 0;
                }
                break;
            case ITEM_TYPES.INCOME:
                if (getFirePassive(fi)) t0_monthlyPassive += monthlyAmount(fi.monthlyIncome, fi.amountFrequency) || 0;
                break;
            default: break;
        }
    });
    // The override replaces only the LIVING expenses; the income view always adds full debt payments on top.
    const t0_requiredExpenses = fireExpenseOverrideEnabled ? fireExpenseOverrideAmount : Math.max(0, t0_monthlyExpenses * 12);
    const t0_incomeRequiredExpenses = t0_requiredExpenses + Math.max(0, t0_monthlyFullDebt * 12);
    const t0_passiveIncome = t0_monthlyPassive * 12;
    const t0_blendedReturn = t0_fireAssets > 0 ? t0_weightedRateNum / t0_fireAssets : 0;
    const t0_fireNumberNominal = fireWithdrawalRate > 0 ? t0_requiredExpenses / fireWithdrawalRate : Infinity;
    const t0_isFIAsset = t0_requiredExpenses <= 0 ? true : t0_fireAssets >= t0_fireNumberNominal * (1 - FIRE_EPS);
    const t0_isFIIncome = t0_incomeRequiredExpenses <= 0 ? true : t0_passiveIncome >= t0_incomeRequiredExpenses * (1 - FIRE_EPS);
    projection.push({
        name: nowLabel,
        portfolioValue: t0_portfolioValue,
        liquidAssets: currentSimulatedLiquidAssets,
        monthlyCashflow: t0_totalCashflow,
        fireAssets: t0_fireAssets,
        requiredExpenses: t0_requiredExpenses,
        incomeRequiredExpenses: t0_incomeRequiredExpenses,
        passiveIncome: t0_passiveIncome,
        fireNumber: (fireWithdrawalRate > 0 && isFinite(t0_fireNumberNominal)) ? t0_fireNumberNominal : null,
        isFIAsset: t0_isFIAsset,
        isFIIncome: t0_isFIIncome,
        fireAssetsNominal: t0_fireAssets,
        requiredExpensesNominal: t0_requiredExpenses,
        incomeRequiredExpensesNominal: t0_incomeRequiredExpenses,
        passiveIncomeNominal: t0_passiveIncome,
        blendedReturnNominal: t0_blendedReturn,
        fireAssetsDrawdown: t0_fireAssets,
        fireAssetsDrawdownNominal: t0_fireAssets,
        isFIAssetDrawdown: t0_isFIAsset,
    });

    const monthsToProject = projectionYears * 12; 
    const baseDate = new Date();
    const monthlyInflationRate = isProjectionInflationAdjusted && typeof projectionInflationRate === 'number' && projectionInflationRate >= 0 ? Math.pow(1 + projectionInflationRate, 1/12) - 1 : 0;
    
    let lastMonthTotalCashflow = t0_totalCashflow;
    // Post-FI drawdown state: once asset-based FI is first reached, switch the FIRE asset line into
    // retirement mode (stop adding, withdraw expenses, keep earning the blended return).
    let drawdownActive = t0_isFIAsset;
    let drawdownBalance = t0_isFIAsset ? t0_fireAssets : 0;

    for (let i = 0; i < monthsToProject; i++) {
        currentSimulatedLiquidAssets += lastMonthTotalCashflow;

        const currentDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + i + 1, 1);
        
        ({ items: simulatedPortfolioItems, liquidAssets: currentSimulatedLiquidAssets } = applyTimelineEvents({
            items: simulatedPortfolioItems,
            liquidAssets: currentSimulatedLiquidAssets,
            currentDate,
            timelineEvents,
            isScenarioActive,
        }));
        
        let periodPortfolioCashflowDelta = 0;
        
        simulatedPortfolioItems.forEach(item => stepItemMonth(item, { rates: resolveItemRates(item, rateOverrides) }));
        
        const periodLiquidInterest = liquidMonthlyInterest(currentSimulatedLiquidAssets, liquidAssetsInterestRate);
        periodPortfolioCashflowDelta += periodLiquidInterest;
        let periodMortgagePayment = 0;
        let periodLoanPayment = 0;

        simulatedPortfolioItems.forEach(item => {
            periodPortfolioCashflowDelta += itemMonthlyCashflow(item, { mode: 'simulated' });
            const paid = amortizeDebtMonth(item);
            if (paid.kind === 'mortgage') periodMortgagePayment += paid.payment;
            else if (paid.kind === 'loan') periodLoanPayment += paid.payment;
        });

        lastMonthTotalCashflow = periodPortfolioCashflowDelta;
        
        let currentItemsPortfolioValue = 0;
        simulatedPortfolioItems.forEach(pItem => {
            let portfolioItemValue = 0; 
            const pItemCurrentValue = parseFloat(pItem.currentValue||0);
          
            if(pItem.type === ITEM_TYPES.REAL_ESTATE || pItem.type === ITEM_TYPES.OTHER_INVESTMENT) {
                portfolioItemValue = isNaN(pItemCurrentValue) ? 0 : pItemCurrentValue;
                if (!isProjectionGross && pItem.type === ITEM_TYPES.REAL_ESTATE && pItem.hasMortgage) {
                    portfolioItemValue -= pItem.simulatedMortgageBalance;
                }
            } else if (pItem.type === ITEM_TYPES.LOAN && !isProjectionGross) {
                portfolioItemValue = -pItem.simulatedLoanBalance;
            }
            currentItemsPortfolioValue += portfolioItemValue;
        });

        const label = formatMonthYear(currentDate);

        // --- FIRE figures for this month (nominal, end-of-month state) ---
        let fireAssetsNominal = currentSimulatedLiquidAssets;
        let fireWeightedRateNum = currentSimulatedLiquidAssets * (parseFloat(liquidAssetsInterestRate) || 0); // Σ(includedValue × annualRate), for the blended drawdown return
        let fireMonthlyExpenses = 0; // common: living + non-rented property costs (debt added separately below)
        let fireMonthlyPassiveIncome = periodLiquidInterest; // liquid interest is always passive
        simulatedPortfolioItems.forEach(fi => {
            const fiVal = parseFloat(fi.currentValue || 0) || 0;
            switch (fi.type) {
                case ITEM_TYPES.REAL_ESTATE:
                    if (getFireInclusion(fi)) {
                        const reEquity = fiVal - (fi.simulatedMortgageBalance || 0);
                        fireAssetsNominal += reEquity;
                        fireWeightedRateNum += reEquity * (parseFloat(fi.annualAppreciationRate) || 0);
                    }
                    if (fi.isRented) {
                        if (getFirePassive(fi)) fireMonthlyPassiveIncome += monthlyAmount(fi.monthlyRentalIncome, fi.amountFrequency) || 0;
                    } else {
                        fireMonthlyExpenses += monthlyAmount(fi.monthlyCosts, fi.amountFrequency) || 0;
                    }
                    break;
                case ITEM_TYPES.OTHER_INVESTMENT:
                    if (getFireInclusion(fi)) {
                        fireAssetsNominal += fiVal;
                        fireWeightedRateNum += fiVal * (fi.isGrowth ? (parseFloat(fi.annualGrowthRate) || 0) : 0);
                    }
                    if (fi.isIncome && getFirePassive(fi)) fireMonthlyPassiveIncome += (fiVal * (parseFloat(fi.annualIncomeYield || 0) || 0)) / 12;
                    break;
                case ITEM_TYPES.EXPENSE:
                    fireMonthlyExpenses += monthlyAmount(fi.monthlyExpense, fi.amountFrequency) || 0;
                    break;
                case ITEM_TYPES.INCOME:
                    if (getFirePassive(fi)) fireMonthlyPassiveIncome += monthlyAmount(fi.monthlyIncome, fi.amountFrequency) || 0;
                    break;
                case ITEM_TYPES.LOAN:
                    fireAssetsNominal -= (fi.simulatedLoanBalance || 0); // FIRE/asset view = net worth: deduct all debt
                    break;
                default: break;
            }
        });
        const overrideNominal = fireExpenseOverrideEnabled ? fireExpenseOverrideAmount * Math.pow(1 + fireInflationRate, (i + 1) / 12) : null;
        // Asset view = net worth: all debt is deducted from assets, so it carries NO debt service. Income view: full payments (asset kept, debt paid from income).
        // The override replaces only the LIVING expenses; the income view always adds full debt payments on top.
        const requiredExpensesNominal = overrideNominal != null ? overrideNominal : Math.max(0, fireMonthlyExpenses * 12);
        const incomeRequiredExpensesNominal = requiredExpensesNominal + Math.max(0, (periodMortgagePayment + periodLoanPayment) * 12);
        const passiveIncomeNominal = fireMonthlyPassiveIncome * 12;
        const fireNumberNominal = fireWithdrawalRate > 0 ? requiredExpensesNominal / fireWithdrawalRate : Infinity;
        const isFIAsset = requiredExpensesNominal <= 0 ? true : fireAssetsNominal >= fireNumberNominal * (1 - FIRE_EPS);
        const isFIIncome = incomeRequiredExpensesNominal <= 0 ? true : passiveIncomeNominal >= incomeRequiredExpensesNominal * (1 - FIRE_EPS);

        // Post-FI drawdown: once assets first reach the FIRE number, "retire" — stop working (salary ends),
        // pay expenses first from the passive income that keeps flowing (rent, yields, pensions), withdraw
        // only the shortfall from the portfolio, and reinvest any surplus. The balance keeps earning its
        // blended return. Liquid interest is excluded from the credited passive income — it is already part
        // of the blended return and would otherwise be double-counted.
        if (!drawdownActive && isFIAsset) {
            drawdownActive = true;
            drawdownBalance = fireAssetsNominal;
        } else if (drawdownActive) {
            const blendedAnnualReturn = fireAssetsNominal > 0 ? fireWeightedRateNum / fireAssetsNominal : 0;
            const drawdownPassiveNominal = Math.max(0, passiveIncomeNominal - periodLiquidInterest * 12);
            drawdownBalance = Math.max(0, drawdownBalance * (1 + blendedAnnualReturn / 12) + (drawdownPassiveNominal - requiredExpensesNominal) / 12);
        }
        const fireAssetsDrawdownNominal = drawdownActive ? drawdownBalance : fireAssetsNominal;
        const isFIAssetDrawdown = requiredExpensesNominal <= 0 ? true : fireAssetsDrawdownNominal >= fireNumberNominal * (1 - FIRE_EPS);

        let displayPortfolioValue = currentItemsPortfolioValue;
        let displayLiquidAssets = currentSimulatedLiquidAssets;
        let displayMonthlyCashflow = periodPortfolioCashflowDelta;
        let displayFireAssets = fireAssetsNominal;
        let displayRequiredExpenses = requiredExpensesNominal;
        let displayPassiveIncome = passiveIncomeNominal;
        let displayIncomeRequiredExpenses = incomeRequiredExpensesNominal;
        let displayFireAssetsDrawdown = fireAssetsDrawdownNominal;

        if (isProjectionInflationAdjusted && monthlyInflationRate > -1 && typeof projectionInflationRate === 'number' && projectionInflationRate >=0) {
            const discountFactor = Math.pow(1 + monthlyInflationRate, i + 1);
            displayPortfolioValue /= discountFactor;
            displayLiquidAssets /= discountFactor;
            displayMonthlyCashflow /= discountFactor;
            displayFireAssets /= discountFactor;
            displayRequiredExpenses /= discountFactor;
            displayPassiveIncome /= discountFactor;
            displayIncomeRequiredExpenses /= discountFactor;
            displayFireAssetsDrawdown /= discountFactor;
        }
        const displayFireNumber = fireWithdrawalRate > 0 ? displayRequiredExpenses / fireWithdrawalRate : null;
        projection.push({
            name: label,
            portfolioValue: displayPortfolioValue,
            liquidAssets: displayLiquidAssets,
            monthlyCashflow: displayMonthlyCashflow,
            fireAssets: displayFireAssets,
            requiredExpenses: displayRequiredExpenses,
            incomeRequiredExpenses: displayIncomeRequiredExpenses,
            passiveIncome: displayPassiveIncome,
            fireNumber: displayFireNumber,
            isFIAsset,
            isFIIncome,
            fireAssetsNominal,
            requiredExpensesNominal,
            incomeRequiredExpensesNominal,
            passiveIncomeNominal,
            fireAssetsDrawdown: displayFireAssetsDrawdown,
            fireAssetsDrawdownNominal,
            isFIAssetDrawdown,
        });
    }
    return projection;
};